// Firebase client. Call initFirebase() once, from the page's main.tsx, choosing the cache:
// - 'memory' for the admin site (staff with private data must not leave it on the device)
// - 'persistent' for the scorekeeper console (queued taps survive reloads and Wi-Fi drops)
//
// No App Check anywhere on the platform: no reCAPTCHA provider, no site key, no debug plumbing, and
// `firebase/app-check` must never be imported again (DECISIONS D39). Do not "fix" this back on its
// own: enforcement is per Firebase product rather than per app, so re-arming one site guards nothing
// and enforcing a product would refuse the other. Staff access rests on Firebase Auth plus
// firestore.rules and storage.rules, as it did before App Check existed.
import { initializeApp, type FirebaseApp } from 'firebase/app'
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth'
import {
  clearIndexedDbPersistence,
  connectFirestoreEmulator,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
  terminate,
  type Firestore,
} from 'firebase/firestore'
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions'
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage'
import { EMULATOR_PORTS, firebaseConfig, FUNCTIONS_REGION, USE_EMULATORS } from './env.ts'

export type CacheMode = 'memory' | 'persistent'

let app: FirebaseApp | null = null
let _auth: Auth | null = null
let _db: Firestore | null = null
let _functions: Functions | null = null
let _storage: FirebaseStorage | null = null
let _mode: CacheMode = 'memory'

export function initFirebase(mode: CacheMode): void {
  if (app) return
  _mode = mode
  app = initializeApp(firebaseConfig)
  _auth = getAuth(app)
  _db = initializeFirestore(app, {
    localCache: mode === 'persistent' ? persistentLocalCache({ tabManager: persistentMultipleTabManager() }) : memoryLocalCache(),
    // Venue Wi-Fi often breaks streaming connections; let the SDK fall back to long polling.
    experimentalAutoDetectLongPolling: true,
  })
  _functions = getFunctions(app, FUNCTIONS_REGION)
  _storage = getStorage(app)
  if (USE_EMULATORS) {
    const p = EMULATOR_PORTS
    connectAuthEmulator(_auth, `http://127.0.0.1:${p.auth}`, { disableWarnings: true })
    connectFirestoreEmulator(_db, '127.0.0.1', p.firestore)
    connectFunctionsEmulator(_functions, '127.0.0.1', p.functions)
    connectStorageEmulator(_storage, '127.0.0.1', p.storage)
  }
}

function need<T>(v: T | null, name: string): T {
  if (!v) throw new Error(`Firebase ${name} used before initFirebase()`)
  return v
}

export const auth = () => need(_auth, 'auth')
export const db = () => need(_db, 'firestore')
export const functions = () => need(_functions, 'functions')
export const storage = () => need(_storage, 'storage')
export const cacheMode = () => _mode

/** Signs out and removes anything Firestore cached on this device. Reloads the page. */
export async function signOutAndWipe(): Promise<void> {
  const a = auth()
  const d = db()
  try {
    await terminate(d)
    if (_mode === 'persistent') await clearIndexedDbPersistence(d)
  } catch {
    // Clearing can fail if another tab still holds the cache; signing out still matters most.
  }
  await a.signOut()
  window.location.assign(_mode === 'persistent' ? '/score/' : '/login')
}
